import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, X, Send, Bot, User, Shield, Minimize2, Maximize2 } from "lucide-react";

const ChatWidget = ({ defaultOpen = false }) => {
  const { token, user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [currentTicket, setCurrentTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastMessageTime, setLastMessageTime] = useState(null);
  const messagesEndRef = useRef(null);

  // Open widget when defaultOpen prop changes
  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true);
    }
  }, [defaultOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
    
    // Update last message time
    if (messages.length > 0) {
      setLastMessageTime(Date.now());
    }
  }, [messages]);

  // Auto-close after 15 minutes of inactivity
  useEffect(() => {
    if (!currentTicket || !lastMessageTime) return;
    
    const checkInactivity = setInterval(() => {
      const inactiveTime = Date.now() - lastMessageTime;
      const fifteenMinutes = 15 * 60 * 1000;
      
      if (inactiveTime > fifteenMinutes && currentTicket.status !== "closed" && currentTicket.status !== "resolved") {
        autoCloseTicket();
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(checkInactivity);
  }, [currentTicket, lastMessageTime]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      setMessages([{
        id: "welcome",
        sender_type: "ai",
        sender_name: "SkillEdge AI Assistant",
        message: "👋 Hi! I'm your SkillEdge AI Assistant. How can I help you today?",
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen]);

  const createTicketInBackground = async (firstMessage) => {
    try {
      const response = await axios.post(`${API}/support/ticket`, {
        subject: firstMessage.substring(0, 50) + "...",
        category: "general",
        message: firstMessage,
        priority: "medium"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentTicket(response.data.ticket);
      
      // Add AI response if provided
      if (response.data.ai_response) {
        setMessages(prev => [...prev, {
          id: response.data.ai_response.id,
          sender_type: "ai",
          sender_name: "SkillEdge AI Assistant",
          message: response.data.ai_response.message,
          timestamp: response.data.ai_response.timestamp,
          confidence_score: response.data.ai_response.confidence_score
        }]);
        
        // Show escalation message if escalated
        if (response.data.escalated) {
          setTimeout(() => {
            setMessages(prev => [...prev, {
              id: "escalation",
              sender_type: "ai",
              sender_name: "System",
              message: "🔄 Your query has been escalated to our support team. A human agent will assist you shortly.",
              timestamp: new Date().toISOString()
            }]);
          }, 1000);
        }
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast.error("Failed to connect to support");
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      sender_type: "user",
      sender_name: user?.name || "You",
      message: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage("");
    setIsLoading(true);

    try {
      // If ticket is closed/resolved, reopen it
      if (currentTicket && (currentTicket.status === "closed" || currentTicket.status === "resolved" || currentTicket.status === "auto_closed")) {
        await reopenTicket(messageToSend);
        setIsLoading(false);
        return;
      }
      
      // If no ticket exists, create one
      if (!currentTicket) {
        await createTicketInBackground(messageToSend);
      } else {
        // Send message to existing ticket
        const response = await axios.post(`${API}/support/chat`, {
          ticket_id: currentTicket.id,
          message: messageToSend
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Add AI response if provided
        if (response.data.ai_response) {
          setMessages(prev => [...prev, {
            id: response.data.ai_response.id,
            sender_type: "ai",
            sender_name: "SkillEdge AI Assistant",
            message: response.data.ai_response.message,
            timestamp: response.data.ai_response.timestamp
          }]);

          // Show feedback after 3rd AI response
          const aiMessageCount = messages.filter(m => m.sender_type === "ai").length;
          if (aiMessageCount >= 2 && !showFeedback) {
            setTimeout(() => {
              setShowFeedback(true);
            }, 2000);
          }

          // Show escalation if happened
          if (response.data.escalated) {
            setTimeout(() => {
              setMessages(prev => [...prev, {
                id: "escalation-" + Date.now(),
                sender_type: "ai",
                sender_name: "System",
                message: "🔄 This conversation has been escalated to our support team for better assistance.",
                timestamp: new Date().toISOString()
              }]);
            }, 1000);
          }
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const requestHumanSupport = async () => {
    if (!currentTicket) {
      toast.error("Please start a conversation first");
      return;
    }

    try {
      await axios.post(`${API}/support/escalate`, {
        ticket_id: currentTicket.id,
        reason: "User requested human support via chat widget"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => [...prev, {
        id: "manual-escalation-" + Date.now(),
        sender_type: "ai",
        sender_name: "System",
        message: "✅ Your conversation has been escalated to our support team. A human agent will respond shortly.",
        timestamp: new Date().toISOString()
      }]);

      toast.success("Connected to human support");
    } catch (error) {
      toast.error("Failed to escalate");
    }
  };

  const submitFeedback = async (wasHelpful) => {
    if (!currentTicket) return;
    
    try {
      await axios.post(`${API}/support/feedback`, {
        ticket_id: currentTicket.id,
        was_helpful: wasHelpful
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowFeedback(false);
      
      if (wasHelpful) {
        setMessages(prev => [...prev, {
          id: "feedback-" + Date.now(),
          sender_type: "ai",
          sender_name: "System",
          message: "✅ Thank you for your feedback! Your issue has been marked as resolved. Feel free to chat with me again anytime!",
          timestamp: new Date().toISOString()
        }]);
        toast.success("Glad I could help!");
      } else {
        // Ask if they want human support
        setMessages(prev => [...prev, {
          id: "feedback-escalate-" + Date.now(),
          sender_type: "ai",
          sender_name: "SkillEdge AI Assistant",
          message: "I'm sorry I couldn't fully help you. Would you like to connect with our support executive for better assistance?",
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      toast.error("Failed to submit feedback");
    }
  };

  const autoCloseTicket = async () => {
    if (!currentTicket) return;
    
    try {
      await axios.patch(`${API}/support/admin/ticket/status`, {
        ticket_id: currentTicket.id,
        status: "auto_closed"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, {
        id: "auto-close-" + Date.now(),
        sender_type: "ai",
        sender_name: "System",
        message: "⏰ This chat has been automatically closed due to inactivity. You can reopen it anytime by sending a new message.",
        timestamp: new Date().toISOString()
      }]);
      
      setCurrentTicket({...currentTicket, status: "auto_closed"});
    } catch (error) {
      console.error("Failed to auto-close ticket:", error);
    }
  };

  const reopenTicket = async (message) => {
    if (!currentTicket) return;
    
    try {
      const response = await axios.post(`${API}/support/reopen`, {
        ticket_id: currentTicket.id,
        message: message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentTicket({...currentTicket, status: "reopened"});
      
      // Add AI response if provided
      if (response.data.ai_response) {
        setMessages(prev => [...prev, {
          id: response.data.ai_response.id,
          sender_type: "ai",
          sender_name: "SkillEdge AI Assistant",
          message: response.data.ai_response.message,
          timestamp: response.data.ai_response.timestamp
        }]);
      }
      
      toast.success("Conversation reopened");
    } catch (error) {
      toast.error("Failed to reopen conversation");
    }
  };

  const closeChat = () => {
    setIsOpen(false);
    // Keep messages so user can reopen and see history
  };

  const resetChat = () => {
    setMessages([{
      id: "welcome",
      sender_type: "ai",
      sender_name: "SkillEdge AI Assistant",
      message: "👋 Hi! I'm your SkillEdge AI Assistant. How can I help you today?",
      timestamp: new Date().toISOString()
    }]);
    setCurrentTicket(null);
  };

  if (!user) return null; // Don't show for logged out users

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-primary hover:bg-primary/90 text-white rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110 z-50 animate-bounce"
          aria-label="Open chat"
        >
          <MessageCircle className="w-7 h-7" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed ${isMinimized ? 'bottom-6 right-6' : 'bottom-6 right-6'} z-50 transition-all duration-300`}>
          <div className={`bg-white rounded-2xl shadow-2xl border border-gray-200 ${
            isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
          } flex flex-col overflow-hidden`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">SkillEdge Support</h3>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    <span>Online</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={closeChat}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex gap-2 ${msg.sender_type === "user" ? "flex-row-reverse" : "flex-row"}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.sender_type === "user" ? "bg-primary text-white" :
                        msg.sender_type === "ai" ? "bg-purple-100 text-purple-600" :
                        "bg-green-100 text-green-600"
                      }`}>
                        {msg.sender_type === "user" ? <User className="w-4 h-4" /> :
                         msg.sender_type === "ai" ? <Bot className="w-4 h-4" /> :
                         <Shield className="w-4 h-4" />}
                      </div>
                      <div className={`flex-1 ${msg.sender_type === "user" ? "text-right" : ""}`}>
                        <div className={`inline-block max-w-[80%] p-3 rounded-lg ${
                          msg.sender_type === "user" ? "bg-primary text-white rounded-br-none" :
                          msg.sender_type === "ai" ? "bg-white text-gray-800 rounded-bl-none shadow-sm" :
                          "bg-blue-50 text-gray-800"
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: "0.1s"}}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: "0.2s"}}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                  
                  {/* Feedback Prompt */}
                  {showFeedback && currentTicket && currentTicket.status !== "resolved" && (
                    <div className="flex justify-center">
                      <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200 max-w-xs">
                        <p className="text-sm font-medium text-center mb-3">Was this helpful?</p>
                        <div className="flex gap-3 justify-center">
                          <button
                            onClick={() => submitFeedback(true)}
                            className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center gap-2"
                          >
                            <span className="text-lg">👍</span>
                            <span>Yes</span>
                          </button>
                          <button
                            onClick={() => submitFeedback(false)}
                            className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
                          >
                            <span className="text-lg">👎</span>
                            <span>No</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                {currentTicket && (
                  <div className="px-4 py-2 bg-white border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={requestHumanSupport}
                        className="text-xs"
                      >
                        <Shield className="w-3 h-3 mr-1" />
                        Human Support
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resetChat}
                        className="text-xs"
                      >
                        New Chat
                      </Button>
                    </div>
                  </div>
                )}

                {/* Input Area */}
                <div className="p-4 bg-white border-t border-gray-200">
                  {currentTicket && (
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={
                        currentTicket.status === "open" ? "bg-blue-100 text-blue-700" :
                        currentTicket.status === "resolved" ? "bg-green-100 text-green-700" :
                        currentTicket.status === "auto_closed" ? "bg-gray-100 text-gray-700" :
                        currentTicket.status === "reopened" ? "bg-purple-100 text-purple-700" :
                        "bg-yellow-100 text-yellow-700"
                      }>
                        {currentTicket.status === "auto_closed" ? "Closed" : currentTicket.status}
                      </Badge>
                      {(currentTicket.status === "resolved" || currentTicket.status === "auto_closed" || currentTicket.status === "closed") && (
                        <span className="text-xs text-gray-500">Send a message to reopen</span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className="flex-1"
                      disabled={isLoading}
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={isLoading || !inputMessage.trim()}
                      className="px-4"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    💡 Powered by AI • Instant responses
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
